import { getLists } from '@/apis/lists';
import { AssignmentSummary, ListSummary } from '@/types/shared';
import {
  AgDataGrid,
  domainCodeAtom,
  PaginationModel,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserDetails } from '@hooks/useUserDetails';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import {
  createContactsTotalFilterOperators,
  createContactsTotalSortComparator,
  withNonEmptyFilterOperators,
} from '@/util/gridFilterOperators';

import { DEFAULT_TABLE_FILTER } from './listsPageTableState';
import { getListTableColumnDefinitionsWithOverrides } from './ListsColumnDefinitions';

const NO_ASSIGNED_LISTS = 'There are no lists assigned to you.';
const NO_ASSIGNED_LISTS_FILTER = 'There are no lists assigned to you shown by the filter.';

const useAssignedLists = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const userSub = user?.sub ?? '';
  return useQuery({
    queryKey: ['lists', userSub, domainCode, 'assigned'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getLists(token, domainCode, '', userSub);
    },
  });
};

const contactsCountFromAssignee = (userId: string, assignees: AssignmentSummary[]) => {
  const assigneeDetails = assignees.find((assignee) => assignee.id === userId) ?? {
    count: 0,
    uncalledCount: 0,
  };
  return `${assigneeDetails.count - assigneeDetails.uncalledCount}/${assigneeDetails.count}`;
};

const listStatusFromAssignee = (userId: string, assignees: AssignmentSummary[]) => {
  const assigneeDetails = assignees.find((assignee) => assignee.id === userId) ?? {
    count: 0,
    uncalledCount: 0,
  };
  return assigneeDetails.uncalledCount === 0
    ? 'Complete'
    : assigneeDetails.uncalledCount === assigneeDetails.count
      ? 'Not started'
      : 'In progress';
};

export const AssignedListsTable = ({ onHomepage }: { onHomepage?: boolean }) => {
  const { user } = useAuth0();

  const [columnVisibilityModelAssignedLists, setColumnVisibilityModelAssignedLists] =
    useState<GridColumnVisibilityModel>({
      id: false,
    });
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 5,
  });

  const { data: assignedLists, isLoading } = useAssignedLists();
  const { data: userDetails } = useUserDetails();

  const alignColumns = !onHomepage && userDetails?.userLevel !== 'Caller';

  const tableColumns = useMemo(() => {
    const columns = getListTableColumnDefinitionsWithOverrides([
      { name: 'List Name', flex: 9 },
      {
        name: 'Contacts',
        flex: 2,
        valueGetter: (value, row) => contactsCountFromAssignee(user?.sub ?? '', row.assignees),
      },
      { name: 'Date Created', flex: alignColumns ? 9 : 3 },
      { name: 'Campaign', flex: alignColumns ? 3 : 9 },
      {
        name: 'Status',
        flex: 4.5,
        valueGetter: (value, row) => listStatusFromAssignee(user?.sub ?? '', row.assignees),
      },
      { name: 'id' },
    ]);
    const userSub = user?.sub ?? '';
    return columns.map((column) => {
      if (column.field === 'contacts') {
        const getAssigneeTotal = (row: ListSummary) => {
          const assignee = row.assignees.find((a) => a.id === userSub);
          return assignee?.count ?? 0;
        };
        const getAssigneeCalled = (row: ListSummary) => {
          const assignee = row.assignees.find((a) => a.id === userSub);
          if (!assignee) return 0;
          return assignee.count - assignee.uncalledCount;
        };
        return {
          ...column,
          filterOperators: createContactsTotalFilterOperators<ListSummary, string, string>(
            getAssigneeTotal
          ),
          sortComparator: createContactsTotalSortComparator<ListSummary>(
            getAssigneeTotal,
            getAssigneeCalled
          ),
        };
      }
      return withNonEmptyFilterOperators(column);
    });
  }, [alignColumns, user?.sub]);

  const skeletonRowWidths = tableColumns.map((column) =>
    columnVisibilityModelAssignedLists && columnVisibilityModelAssignedLists[column.field] === false
      ? 0
      : (column.flex ?? 0)
  );
  return (
    <AgDataGrid
      autoTableHeight={false}
      sx={[
        onHomepage
          ? {
              border: 0,
            }
          : {
              border: null,
            },
        onHomepage
          ? {
              '.MuiDataGrid-cell': {
                paddingLeft: 0,
              },
            }
          : {
              '.MuiDataGrid-cell': {
                paddingLeft: null,
              },
            },
        onHomepage
          ? {
              '.MuiDataGrid-columnHeader': {
                paddingLeft: 0,
              },
            }
          : {
              '.MuiDataGrid-columnHeader': {
                paddingLeft: null,
              },
            },
      ]}
      loading={isLoading}
      rows={assignedLists ?? []}
      columns={tableColumns}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[5]}
      noRowsMessage={NO_ASSIGNED_LISTS}
      noResultsMessage={NO_ASSIGNED_LISTS_FILTER}
      skeletonColumnWidths={skeletonRowWidths}
      columnVisibilityModel={columnVisibilityModelAssignedLists}
      onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModelAssignedLists(newModel)}
      initialState={DEFAULT_TABLE_FILTER}
    />
  );
};
